// Écran classement : plein canvas, cohérent avec les autres écrans du jeu (screens.ts).
// Charge apiLeaderboard() puis peint, niveau par niveau, les 5 meilleurs temps de la
// famille (PSEUDO  TEMPS  MORTS), déjà triés par le serveur (temps croissant). Le contenu
// défile (flèches haut/bas, ou glisser vertical au doigt/à la souris) s'il dépasse la
// hauteur du canvas. « (aucun score) » pour un niveau vide ; « Classement indisponible »
// sur panne réseau ou 401. Échap (ou un tap sans glissement) rend la main.
// En tête défilent deux sections transverses : « En ligne » (stats versus/coop + derniers
// matchs) et « Campagnes » (meilleur temps solo + réussites coop + meilleur duelliste par campagne) ; chacune est
// simplement absente si son chargement (apiMatches / apiListCampaigns) échoue.
import type { CampaignSummary, LeaderboardResponse, MatchesResponse } from '../shared/api-types'
import { apiLeaderboard, apiListCampaigns, apiMatches } from './api'
import { isTouch } from './platform'
import { formatTime } from './format-time'
import { titleLabel } from './loadout'

const BG = '#0d0b1a'
const FG = '#f0e9d2'
const ACCENT = '#ffd93d'
const DIM = '#7a7599'
const FONT = 'ui-monospace, Menlo, monospace'

const TOP_N = 5

// Hauteurs (px) des lignes du contenu défilant ; servent au calcul de la hauteur totale
// (donc du défilement maximal) ET au rendu, pour que les deux ne divergent jamais.
const TITLE_H = 46, COLHEAD_H = 30, ROW_H = 34, EMPTY_H = 34, GAP = 30
const STEP = 64 // pas de défilement par appui sur une flèche
// Colonnes (x px) partagées par les blocs de niveau ET la section « En ligne » : rang à
// droite de RANK_X, libellés à partir de PSEUDO_X, deux colonnes chiffrées à droite.
const RANK_X = 306, PSEUDO_X = 340, TEMPS_X = 906, MORTS_X = 1052
// Au plus 5 derniers matchs affichés dans la section « En ligne » (comme TOP_N pour les niveaux).
const RECENT_N = 5

// mm:ss — même granularité que le HUD et l'écran de victoire (cohérence d'affichage).

// Hauteur de la section « En ligne » (0 si absente : erreur réseau ou pas encore chargée) :
// titre + lignes joueurs + (en-tête « derniers matchs » + lignes) ; ou titre + marqueur
// « aucun match ». DOIT rester le miroir exact de paintOnline (sinon défilement faux).
function onlineHeight(m: MatchesResponse | null): number {
  if (!m) return 0
  if (m.summary.length === 0 && m.recent.length === 0) return TITLE_H + EMPTY_H + GAP
  let h = TITLE_H + m.summary.length * ROW_H
  const recent = Math.min(m.recent.length, RECENT_N)
  if (recent > 0) h += COLHEAD_H + recent * ROW_H
  return h + GAP
}

// Hauteur de la section « Campagnes » (0 si absente : erreur réseau ou pas encore chargée) :
// titre + une ligne par campagne, ou titre + marqueur « Aucune campagne ». Miroir exact de
// paintCampaigns (sinon défilement faux), comme onlineHeight l'est de paintOnline.
function campaignsHeight(c: CampaignSummary[] | null): number {
  if (!c) return 0
  if (c.length === 0) return TITLE_H + EMPTY_H + GAP
  return TITLE_H + c.length * ROW_H + GAP
}

// Hauteur totale du contenu : sections « En ligne » puis « Campagnes » (avant les niveaux) PUIS
// chaque bloc de niveau (titre + en-tête de colonnes + lignes de score ou marqueur vide + espace).
function contentHeight(d: LeaderboardResponse, m: MatchesResponse | null, c: CampaignSummary[] | null): number {
  let h = onlineHeight(m) + campaignsHeight(c)
  for (const lvl of d.levels) {
    const n = Math.min(lvl.scores.length, TOP_N)
    h += TITLE_H + COLHEAD_H + (n > 0 ? n * ROW_H : EMPTY_H) + GAP
  }
  return h
}

/**
 * Ouvre l'écran classement. Charge le classement, l'affiche, gère le défilement clavier.
 * Échap rend la main via onQuit (et détache l'écouteur). `sheet` n'est pas utilisé ici
 * (rendu 100 % texte) mais reste dans la signature pour l'homogénéité des écrans.
 */
export function startLeaderboard(
  canvas: HTMLCanvasElement,
  _sheet: HTMLImageElement,
  onQuit: () => void,
): void {
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  let data: LeaderboardResponse | null = null
  // Historique des parties en ligne : null = section absente (erreur réseau silencieuse ou
  // pas encore chargée). Chargé en parallèle du classement ; une panne le laisse à null.
  let matches: MatchesResponse | null = null
  // Campagnes (meilleur temps solo + réussites coop) : null = section absente (erreur réseau ou
  // pas encore chargée), [] = section « Aucune campagne ». Chargé en parallèle, panne → null.
  let campaigns: CampaignSummary[] | null = null
  let status: 'loading' | 'ready' | 'error' = 'loading'
  let scroll = 0
  let maxScroll = 0
  let left = false

  function leave(): void {
    if (left) return
    left = true
    removeEventListener('keydown', onKey)
    canvas.removeEventListener('pointerdown', onPointerDown)
    canvas.removeEventListener('pointermove', onPointerMove)
    canvas.removeEventListener('pointerup', onPointerUp)
    canvas.removeEventListener('pointercancel', onPointerCancel)
    canvas.removeEventListener('click', onTap)
    onQuit()
  }

  // Bande de pied de page : rappel des commandes.
  function drawFooter(W: number, H: number): void {
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillStyle = DIM; ctx.font = `20px ${FONT}`
    const help = isTouch ? 'Glisse pour défiler, touche pour revenir' : 'Flèches haut/bas pour défiler, Échap pour revenir'
    ctx.fillText(help, W / 2, H - 34)
  }

  // Chevron indiquant qu'il reste du contenu au-dessus (dir < 0) ou en-dessous (dir > 0).
  function drawArrow(cx: number, cy: number, dir: number): void {
    ctx.fillStyle = ACCENT
    ctx.beginPath()
    const w = 15, h = 9
    if (dir < 0) { ctx.moveTo(cx - w, cy + h); ctx.lineTo(cx + w, cy + h); ctx.lineTo(cx, cy - h) }
    else { ctx.moveTo(cx - w, cy - h); ctx.lineTo(cx + w, cy - h); ctx.lineTo(cx, cy + h) }
    ctx.closePath(); ctx.fill()
  }

  // Dessine les blocs de niveau dans le repère du contenu (y commençant à 0). L'appelant
  // a déjà translaté/clippé le contexte sur la zone visible.
  // Section « En ligne », peinte AVANT les niveaux dans le repère du contenu (y depuis y0).
  // Renvoie le y après la section (le point de départ du 1er bloc de niveau). Miroir exact
  // de onlineHeight. Rien si matches est null (section absente).
  function paintOnline(y0: number): number {
    const m = matches
    if (!m) return y0
    let y = y0
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'; ctx.fillStyle = ACCENT; ctx.font = `bold 30px ${FONT}`
    ctx.fillText('EN LIGNE', RANK_X, y + TITLE_H / 2)
    y += TITLE_H
    if (m.summary.length === 0 && m.recent.length === 0) {
      ctx.fillStyle = DIM; ctx.font = `24px ${FONT}`; ctx.textAlign = 'left'
      ctx.fillText('Aucun match en ligne pour l\'instant', PSEUDO_X, y + EMPTY_H / 2)
      return y + EMPTY_H + GAP
    }
    // Joueurs : « pseudo   <wins> V / <losses> D   <coops> coop » (triés wins desc par le serveur).
    m.summary.forEach((p, i) => {
      const cy = y + ROW_H / 2
      const first = i === 0
      ctx.font = `${first ? 'bold ' : ''}26px ${FONT}`
      ctx.textAlign = 'left'; ctx.fillStyle = first ? ACCENT : FG
      ctx.fillText(p.pseudo, PSEUDO_X, cy)                 // SÉCURITÉ pseudo via fillText
      ctx.textAlign = 'right'; ctx.fillStyle = FG
      ctx.fillText(`${p.wins} V / ${p.losses} D`, TEMPS_X, cy)
      ctx.fillStyle = p.coops > 0 ? FG : DIM
      ctx.fillText(`${p.coops} coop`, MORTS_X, cy)
      y += ROW_H
    })
    // Derniers matchs : « p1 g1-g2 p2 · levelName », le vainqueur en accent (jaune).
    const recent = m.recent.slice(0, RECENT_N)
    if (recent.length > 0) {
      ctx.fillStyle = DIM; ctx.font = `18px ${FONT}`; ctx.textAlign = 'left'
      ctx.fillText('DERNIERS MATCHS', PSEUDO_X, y + COLHEAD_H / 2)
      y += COLHEAD_H
      for (const mt of recent) {
        const cy = y + ROW_H / 2
        ctx.font = `22px ${FONT}`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
        // Segments enchaînés (measureText) pour ne colorer que le nom du vainqueur.
        let x = PSEUDO_X
        ctx.fillStyle = mt.winner === mt.p1 ? ACCENT : FG
        ctx.fillText(mt.p1, x, cy); x += ctx.measureText(mt.p1).width   // SÉCURITÉ pseudo
        const score = ` ${mt.gold1}-${mt.gold2} `
        ctx.fillStyle = DIM
        ctx.fillText(score, x, cy); x += ctx.measureText(score).width
        ctx.fillStyle = mt.winner === mt.p2 ? ACCENT : FG
        ctx.fillText(mt.p2, x, cy); x += ctx.measureText(mt.p2).width   // SÉCURITÉ pseudo
        ctx.fillStyle = DIM
        ctx.fillText(` · ${mt.levelName}`, x, cy)                        // SÉCURITÉ nom de carte
        y += ROW_H
      }
    }
    return y + GAP
  }

  // Section « Campagnes », peinte APRÈS « En ligne » et AVANT les niveaux (repère du contenu,
  // y depuis y0). Renvoie le y après la section. Miroir exact de campaignsHeight. Rien si
  // campaigns est null (section absente). Chaque campagne : nom · meilleur temps solo (pseudo) ·
  // réussites coop · meilleur duelliste (étape 2) à l'extrême droite. SÉCURITÉ : noms de campagne
  // et pseudos (origine utilisateur) peints via fillText, JAMAIS innerHTML.
  function paintCampaigns(y0: number): number {
    const c = campaigns
    if (!c) return y0
    let y = y0
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'; ctx.fillStyle = ACCENT; ctx.font = `bold 30px ${FONT}`
    ctx.fillText('CAMPAGNES', RANK_X, y + TITLE_H / 2)
    y += TITLE_H
    if (c.length === 0) {
      ctx.fillStyle = DIM; ctx.font = `24px ${FONT}`; ctx.textAlign = 'left'
      ctx.fillText('Aucune campagne pour l\'instant', PSEUDO_X, y + EMPTY_H / 2)
      return y + EMPTY_H + GAP
    }
    for (const camp of c) {
      const cy = y + ROW_H / 2
      ctx.font = `26px ${FONT}`
      ctx.textAlign = 'left'; ctx.fillStyle = FG
      ctx.fillText(camp.name, PSEUDO_X, cy)                          // SÉCURITÉ nom de campagne
      ctx.textAlign = 'right'
      if (camp.soloBest) {
        ctx.fillStyle = FG
        ctx.fillText(`${formatTime(camp.soloBest.bestMs)} (${camp.soloBest.pseudo})`, TEMPS_X, cy) // SÉCURITÉ pseudo
      } else {
        ctx.fillStyle = DIM
        ctx.fillText('solo : aucun', TEMPS_X, cy)
      }
      ctx.fillStyle = camp.coopWins > 0 ? FG : DIM
      ctx.fillText(`${camp.coopWins} coop`, MORTS_X, cy)
      // Victoires en DUEL (étape 2) : meilleur duelliste de la campagne, à l'extrême droite
      // (dans la marge libre à droite de la colonne coop). Grisé si personne n'a encore gagné
      // de duel. versusWins est trié décroissant côté serveur : [0] = le meilleur. SÉCURITÉ :
      // pseudo via fillText (jamais innerHTML).
      const topDuel = camp.versusWins[0]
      ctx.fillStyle = topDuel ? FG : DIM
      // Pseudo borné (12 car. + …) : sans mesure de texte, un pseudo très long (jusqu'à 20 car.)
      // pourrait chevaucher la colonne coop à gauche. Les pseudos famille font < 10 car. (jamais
      // tronqués) ; le plafond garde la colonne bornée quel que soit le pseudo (finding revue).
      const duelPseudo = topDuel ? (topDuel.pseudo.length > 12 ? `${topDuel.pseudo.slice(0, 12)}…` : topDuel.pseudo) : ''
      ctx.fillText(
        topDuel ? `${duelPseudo} ${topDuel.versusWins} duel${topDuel.versusWins > 1 ? 's' : ''}` : 'aucun duel',
        ctx.canvas.width - 24,
        cy,
      )
      y += ROW_H
    }
    return y + GAP
  }

  function paintContent(d: LeaderboardResponse): void {
    const rankX = RANK_X, pseudoX = PSEUDO_X, tempsX = TEMPS_X, mortsX = MORTS_X
    let y = paintOnline(0) // section « En ligne » avant les niveaux
    y = paintCampaigns(y)  // section « Campagnes » avant les niveaux
    for (const lvl of d.levels) {
      ctx.textBaseline = 'middle'
      // Titre du niveau
      ctx.textAlign = 'left'; ctx.fillStyle = ACCENT; ctx.font = `bold 30px ${FONT}`
      ctx.fillText(lvl.title, rankX, y + TITLE_H / 2)
      y += TITLE_H

      // En-tête de colonnes
      ctx.fillStyle = DIM; ctx.font = `18px ${FONT}`
      ctx.textAlign = 'left'; ctx.fillText('PSEUDO', pseudoX, y + COLHEAD_H / 2)
      ctx.textAlign = 'right'
      ctx.fillText('TEMPS', tempsX, y + COLHEAD_H / 2)
      ctx.fillText('MORTS', mortsX, y + COLHEAD_H / 2)
      y += COLHEAD_H

      const scores = lvl.scores.slice(0, TOP_N)
      if (scores.length === 0) {
        ctx.textAlign = 'left'; ctx.fillStyle = DIM; ctx.font = `24px ${FONT}`
        ctx.fillText('(aucun score)', pseudoX, y + EMPTY_H / 2)
        y += EMPTY_H
      } else {
        scores.forEach((row, i) => {
          const cy = y + ROW_H / 2
          const first = i === 0
          ctx.font = `${first ? 'bold ' : ''}26px ${FONT}`
          ctx.textAlign = 'right'; ctx.fillStyle = first ? ACCENT : DIM
          ctx.fillText(String(i + 1), rankX, cy)          // rang
          ctx.textAlign = 'left'; ctx.fillStyle = first ? ACCENT : FG
          // Badges de MODE (records par mode, 16/07) : ⚡ = dash actif, 🔦 = torche active.
          const nameStr = row.pseudo + (row.dash ? ' ⚡' : '') + (row.torch ? ' 🔦' : '')
          ctx.fillText(nameStr, pseudoX, cy)
          // TITRE équipé (Lot C, « affiché au classement », 21/07) : libellé dim en italique après le
          // pseudo, tronqué (…) pour ne jamais mordre la colonne TEMPS. titleLabel retire « Titre : » ;
          // row.title null (aucun titre équipé) -> rien affiché.
          const title = titleLabel(row.title)
          if (title) {
            const afterX = pseudoX + ctx.measureText(nameStr).width + 14
            ctx.save()
            ctx.font = `italic 19px ${FONT}`
            ctx.fillStyle = DIM
            const maxRight = tempsX - 150 // marge pour laisser respirer la colonne TEMPS
            let lbl = '· ' + title
            while (lbl.length > 3 && afterX + ctx.measureText(lbl).width > maxRight) lbl = lbl.slice(0, -2) + '…'
            ctx.fillText(lbl, afterX, cy)
            ctx.restore()
          }
          ctx.textAlign = 'right'; ctx.fillStyle = FG
          ctx.fillText(formatTime(row.bestMs), tempsX, cy) // temps
          ctx.fillStyle = row.deaths === 0 ? DIM : FG
          ctx.fillText(String(row.deaths), mortsX, cy)     // morts
          y += ROW_H
        })
      }
      y += GAP
    }
  }

  function paint(): void {
    const W = canvas.width, H = canvas.height
    ctx.fillStyle = BG; ctx.fillRect(0, 0, W, H)

    // Titre fixe
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillStyle = ACCENT; ctx.font = `bold 60px ${FONT}`
    ctx.fillText('CLASSEMENT', W / 2, 64)

    if (status !== 'ready' || !data) {
      ctx.fillStyle = FG; ctx.font = `32px ${FONT}`
      ctx.fillText(status === 'loading' ? 'Chargement...' : 'Classement indisponible', W / 2, H / 2)
      drawFooter(W, H)
      return
    }

    const top = 140, bottom = 724, viewH = bottom - top
    maxScroll = Math.max(0, contentHeight(data, matches, campaigns) - viewH)
    scroll = Math.min(Math.max(scroll, 0), maxScroll)

    ctx.save()
    ctx.beginPath(); ctx.rect(0, top, W, viewH); ctx.clip()
    ctx.translate(0, top - scroll)
    paintContent(data)
    ctx.restore()

    if (scroll > 0) drawArrow(W / 2, 126, -1)          // encore du contenu au-dessus
    if (scroll < maxScroll) drawArrow(W / 2, 738, 1)   // encore du contenu en-dessous
    drawFooter(W, H)
  }

  function onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') { e.preventDefault(); leave(); return }
    if (status !== 'ready') return
    if (e.code === 'ArrowUp') { e.preventDefault(); scroll -= STEP; paint() }
    else if (e.code === 'ArrowDown') { e.preventDefault(); scroll += STEP; paint() }
    else if (e.code === 'PageUp') { e.preventDefault(); scroll -= viewStep(); paint() }
    else if (e.code === 'PageDown') { e.preventDefault(); scroll += viewStep(); paint() }
    else if (e.code === 'Home') { e.preventDefault(); scroll = 0; paint() }
    else if (e.code === 'End') { e.preventDefault(); scroll = maxScroll; paint() }
  }
  function viewStep(): number { return 724 - 140 - ROW_H } // presque une page

  // Glisser vertical (doigt ou souris, bouton tenu) = défiler ; relâcher sans avoir
  // glissé (déplacement total < 10 px canvas) = tap = retour. Le delta est converti en
  // coordonnées canvas via getBoundingClientRect (même échelle que le hit-test des menus
  // dans screens.ts) et borné par paint(), exactement comme le défilement aux flèches.
  let dragId = -1        // pointerId suivi (-1 : aucun doigt/bouton posé)
  let dragClientX = 0    // clientX au pointerdown (une glisse horizontale n'est pas un tap)
  let dragClientY = 0    // clientY au pointerdown
  let dragScroll = 0     // scroll au pointerdown
  let dragDist = 0       // plus grand déplacement (X ou Y) vu pendant le geste, en px canvas

  function dragDy(e: PointerEvent): number {
    const rect = canvas.getBoundingClientRect()
    if (rect.height === 0) return 0
    return ((dragClientY - e.clientY) / rect.height) * canvas.height
  }
  function onPointerDown(e: PointerEvent): void {
    e.preventDefault()
    dragId = e.pointerId
    dragClientX = e.clientX
    dragClientY = e.clientY
    dragScroll = scroll
    dragDist = 0
    canvas.setPointerCapture(e.pointerId)
  }
  function onPointerMove(e: PointerEvent): void {
    if (dragId !== e.pointerId) return
    const rect = canvas.getBoundingClientRect()
    const dx = rect.width === 0 ? 0 : ((e.clientX - dragClientX) / rect.width) * canvas.width
    const dy = dragDy(e)
    dragDist = Math.max(dragDist, Math.abs(dy), Math.abs(dx))
    if (status !== 'ready') return // rien à défiler pendant Chargement/erreur
    scroll = dragScroll + dy
    paint() // paint() borne scroll dans [0, maxScroll], comme pour les flèches
  }
  function onPointerUp(e: PointerEvent): void {
    if (dragId === e.pointerId) dragId = -1
  }
  function onPointerCancel(e: PointerEvent): void {
    if (dragId === e.pointerId) dragId = -1
  }
  // Le retour passe par le click (émis par le navigateur après un relâchement resté sur
  // le canvas) et non par pointerup : l'écran suivant (menu) attache son propre écouteur
  // click sur CE canvas pendant leave(), et un écouteur ajouté pendant le dispatch d'un
  // événement n'est pas appelé pour cet événement. Via pointerup, le click « fantôme »
  // qui suit le tap sélectionnerait une entrée du menu. Même mécanique que la sortie
  // game-over de solo.ts (elle aussi sur click).
  function onTap(e: MouseEvent): void {
    if (dragDist >= 10) return // on vient de glisser : ce n'est pas un tap
    e.preventDefault()
    leave()
  }

  addEventListener('keydown', onKey)
  canvas.addEventListener('pointerdown', onPointerDown)
  canvas.addEventListener('pointermove', onPointerMove)
  canvas.addEventListener('pointerup', onPointerUp)
  canvas.addEventListener('pointercancel', onPointerCancel)
  canvas.addEventListener('click', onTap)
  paint() // « Chargement... » immédiat
  // Classement, historique en ligne ET campagnes chargés EN PARALLÈLE. Une panne de l'historique
  // (apiMatches) ou des campagnes (apiListCampaigns) les laisse à null → section correspondante
  // simplement absente ; seule une panne du classement (apiLeaderboard) fait basculer l'écran
  // en « indisponible ».
  Promise.all([apiLeaderboard(), apiMatches().catch(() => null), apiListCampaigns().catch(() => null)])
    .then(([lb, m, c]) => { if (left) return; data = lb; matches = m; campaigns = c; status = 'ready'; paint() })
    .catch(() => { if (left) return; status = 'error'; paint() })
}
